var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { on, off, contains } from '../_util/dom';
import { isFunction } from '../_util/is';
import Portal from '../Portal';
import ResizeObserver from '../_util/resizeObserver';
import cs from '../_util/classNames';
import { ConfigContext } from '../ConfigProvider';
import getStyle from './getPopupStyle';
import throttleByRaf from '../_util/throttleByRaf';
var positionList = [
    'top',
    'tl',
    'tr',
    'bottom',
    'bl',
    'br',
    'left',
    'lt',
    'lb',
    'right',
    'rt',
    'rb',
];
var triggerList = ['hover', 'click', 'focus', 'manual'];
function splitChildrenStyle(obj, keys) {
    var picked = {};
    var omitted = __assign({}, obj);
    keys.forEach(function (key) {
        if (obj && key in obj) {
            picked[key] = obj[key];
            delete omitted[key];
        }
    });
    return { picked: picked, omitted: omitted };
}
var Trigger = /** @class */ (function (_super) {
    __extends(Trigger, _super);
    function Trigger(props) {
        var _this = _super.call(this, props) || this;
        _this.delayTimer = null;
        _this.updatePositionTimer = null;
        // is popup open?
        _this.popupOpen = false;
        // if mousedown to hide popup, ignore onFocus
        _this.mousedownToHide = false;
        _this.hasPopupMouseDown = false;
        _this.unmount = false;
        _this.getRootElement = function () {
            return findDOMNode(_this);
        };
        _this.componentWillReceiveProps = function (nextProps) {
            // 防止 setPopupVisible 触发两次，因为 clickOutside 会多触发一次。
            if ('popupVisible' in nextProps &&
                nextProps.popupVisible !== undefined &&
                nextProps.popupVisible !== _this.popupOpen) {
                _this.popupOpen = nextProps.popupVisible;
                _this.setState({
                    popupVisible: nextProps.popupVisible,
                });
                _this.setPopupVisible(nextProps.popupVisible);
            }
        };
        _this.offClickOutside = function () {
            if (_this.handleClickOutside) {
                var getDocument = _this.props.getDocument;
                var root = isFunction(getDocument) && getDocument();
                off(root, 'mousedown', _this.clickOutside);
                _this.handleClickOutside = false;
            }
        };
        _this.clickOutside = function (e) {
            var _a = _this.props, trigger = _a.trigger, onClickOutside = _a.onClickOutside;
            var triggerNode = findDOMNode(_this.triggerRef);
            var childrenDom = findDOMNode(_this);
            if (!contains(triggerNode, e.target) &&
                !contains(childrenDom, e.target) &&
                !_this.hasPopupMouseDown) {
                if (trigger !== 'manual') {
                    _this.popupOpen = false;
                    _this.setPopupVisible(false);
                }
                onClickOutside && onClickOutside();
            }
        };
        _this.getTransformOrigin = function (position) {
            var content = findDOMNode(_this.triggerRef);
            if (!content)
                return {};
            var transformOrigin = {
                top: '50% 100% 0',
                tl: '15px 100% 0',
                tr: content.clientWidth - 15 + "px 100% 0",
                bottom: '50% 0 0',
                bl: '15px 0 0',
                br: content.clientWidth - 15 + "px 0 0",
                left: '100% 50% 0',
                lt: '100% 15px 0',
                lb: "100% " + (content.clientHeight - 15) + "px 0",
                right: '0 50% 0',
                rt: '0 15px 0',
                rb: "0 " + (content.clientHeight - 15) + "px 0",
            };
            if (_this.props.classNames === 'zoomTooltip') {
                return {
                    transformOrigin: transformOrigin[position],
                };
            }
            if (_this.props.classNames === 'dropdownSlide') {
                var origin_1 = '0% 0%';
                if (['top', 'tl', 'tr'].indexOf(position) > -1) {
                    origin_1 = '100% 100%';
                }
                return {
                    transformOrigin: origin_1,
                };
            }
            return {};
        };
        _this.getPopupStyle = function () {
            if (_this.unmount) {
                return;
            }
            var mountContainer = _this.getContainer();
            var content = findDOMNode(_this.triggerRef);
            var child = findDOMNode(_this);
            var _a = getStyle(_this.props, content, child, mountContainer), style = _a.style, arrowStyle = _a.arrowStyle, realPosition = _a.realPosition;
            _this.realPosition = realPosition || _this.props.position;
            _this.arrowStyle = arrowStyle || {};
            return __assign(__assign({}, style), _this.getTransformOrigin(_this.realPosition));
        };
        _this.showPopup = function (callback) {
            if (callback === void 0) { callback = function () { }; }
            _this.setState({
                popupVisible: true,
            }, function () {
                var popupStyle = _this.getPopupStyle();
                _this.setState({
                    popupStyle: popupStyle,
                }, callback);
            });
        };
        _this.update = throttleByRaf(function (callback) {
            var popupStyle = _this.getPopupStyle();
            _this.setState({
                popupStyle: popupStyle,
            }, function () {
                callback && callback();
            });
        });
        _this.updatePopupPosition = function (delay, callback) {
            if (delay === void 0) { delay = 0; }
            if (delay < 4) {
                _this.updatePositionTimer = _this.update(callback);
                return;
            }
            _this.updatePositionTimer = setTimeout(function () {
                var popupStyle = _this.getPopupStyle();
                _this.setState({
                    popupStyle: popupStyle,
                }, function () {
                    callback && callback();
                });
            }, delay);
        };
        _this.setPopupVisible = function (visible, delay, callback) {
            if (delay === void 0) { delay = 0; }
            var onVisibleChange = _this.props.onVisibleChange;
            if (visible !== _this.state.popupVisible) {
                _this.popupOpen = visible;
                _this.delayToDo(delay, function () {
                    onVisibleChange && onVisibleChange(visible);
                    if (visible) {
                        _this.showPopup(callback);
                    }
                    else {
                        _this.setState({
                            popupVisible: false,
                        }, function () {
                            callback && callback();
                        });
                    }
                });
            }
        };
        _this.delayToDo = function (delay, callback) {
            if (delay) {
                _this.clearDelayTimer();
                _this.delayTimer = setTimeout(function () {
                    callback();
                    _this.clearDelayTimer();
                }, delay);
            }
            else {
                callback();
            }
        };
        _this.onMouseEnter = function (e) {
            var mouseEnterDelay = _this.props.mouseEnterDelay;
            var child = _this.getChild();
            _this.clearDelayTimer();
            _this.setPopupVisible(true, mouseEnterDelay || 0, function () {
                child.props.onMouseEnter && child.props.onMouseEnter(e);
            });
        };
        _this.onMouseLeave = function (e) {
            var mouseLeaveDelay = _this.props.mouseLeaveDelay;
            var child = _this.getChild();
            _this.clearDelayTimer();
            if (_this.state.popupVisible) {
                _this.setPopupVisible(false, mouseLeaveDelay || 0, function () {
                    child.props.onMouseLeave && child.props.onMouseLeave(e);
                });
            }
        };
        _this.onPopupMouseEnter = function () {
            _this.clearDelayTimer();
        };
        _this.onPopupMouseLeave = function (e) {
            _this.onMouseLeave(e);
        };
        _this.onClick = function (e) {
            var popupVisible = _this.state.popupVisible;
            var _a = _this.props, onClick = _a.onClick, clickToClose = _a.clickToClose;
            var child = _this.getChild();
            if (popupVisible) {
                _this.mousedownToHide = true;
            }
            if ((clickToClose && popupVisible) || !popupVisible) {
                _this.setPopupVisible(!popupVisible, 0, function () {
                    onClick && onClick(_this.state.popupVisible);
                    child.props.onClick && child.props.onClick(e);
                });
            }
        };
        _this.onFocus = function () {
            var focusDelay = _this.props.focusDelay;
            var child = _this.getChild();
            var onFocus = child && child.props && child.props.onFocus;
            _this.clearDelayTimer();
            if (!_this.mousedownToHide) {
                if (_this.state.popupVisible) {
                    onFocus && onFocus();
                }
                else {
                    _this.setPopupVisible(true, focusDelay || 0, onFocus);
                }
            }
            _this.mousedownToHide = false;
        };
        _this.onBlur = function () {
            var child = _this.getChild();
            var onBlur = child && child.props && child.props.onBlur;
            _this.setPopupVisible(false, 200);
            onBlur && onBlur();
        };
        _this.onResize = function () {
            if (_this.props.autoFixPosition) {
                _this.updatePopupPosition();
            }
        };
        _this.onPopupMouseDown = function () {
            _this.hasPopupMouseDown = true;
            clearTimeout(_this.mouseDownTimeout);
            _this.mouseDownTimeout = setTimeout(function () {
                _this.hasPopupMouseDown = false;
            }, 0);
        };
        // 当 children 中的元素 disabled 时，不能正确触发 hover 等事件，所以当监测到对应
        // 组件有 disabled 时，给元素加一层 span，处理事件，模拟样式
        _this.getChild = function () {
            var children = _this.props.children;
            var element = children;
            var elementType = (element && typeof element !== 'string' && element.type);
            var child = children;
            if (typeof children === 'string') {
                child = React.createElement("span", null, children);
            }
            else if (element &&
                (elementType.__BYTE_BUTTON === true ||
                    elementType.__BYTE_CHECKBOX === true ||
                    elementType.__BYTE_SWITCH === true ||
                    elementType.__BYTE_RADIO === true ||
                    elementType === 'button') &&
                element.props.disabled) {
                // 从样式中提取出会影响布局的到上层 span 样式中。
                var _a = splitChildrenStyle(element.props.style, [
                    'position',
                    'left',
                    'right',
                    'top',
                    'bottom',
                    'float',
                    'display',
                    'zIndex',
                ]), picked = _a.picked, omitted = _a.omitted;
                child = (React.createElement("span", { className: element.props.className, style: __assign(__assign({ display: 'inline-block' }, picked), { cursor: 'not-allowed' }) }, React.cloneElement(element, {
                    style: __assign(__assign({}, omitted), { pointerEvents: 'none' }),
                    className: undefined,
                })));
            }
            // 防止为空报错
            return child || React.createElement("span", null);
        };
        _this.getContainer = function () {
            var getGlobalPopupContainer = _this.context.getPopupContainer;
            var getPopupContainer = _this.props.getPopupContainer;
            var gpc = getPopupContainer || getGlobalPopupContainer;
            return gpc(_this.getRootElement());
        };
        _this.state = {
            popupVisible: !!props.popupVisible,
            popupStyle: {},
        };
        _this.popupOpen = !!props.popupVisible;
        return _this;
    }
    Trigger.prototype.componentDidMount = function () {
        this.componentDidUpdate();
    };
    Trigger.prototype.componentDidUpdate = function () {
        var popupVisible = this.state.popupVisible;
        var _a = this.props, getDocument = _a.getDocument, blurToHide = _a.blurToHide;
        if (!popupVisible) {
            this.offClickOutside();
            return;
        }
        if (!this.handleClickOutside && blurToHide) {
            var root = isFunction(getDocument) && getDocument();
            if (root) {
                // clickOutside 必须监听mousedown。
                // 1. 如果事件目标元素在click后被移除，document.onclick被触发时已经没有该元素，会错误触发clickOutside逻辑，隐藏popup。
                // 2. 点击label标签，会触发对应input元素的点击事件，导致触发clickOutside，隐藏popup。
                on(root, 'mousedown', this.clickOutside);
                this.handleClickOutside = true;
            }
        }
    };
    Trigger.prototype.componentWillUnmount = function () {
        this.offClickOutside();
        this.clearTimer();
        this.unmount = true;
    };
    Trigger.prototype.clearTimer = function () {
        if (this.updatePositionTimer) {
            if (this.updatePositionTimer.cancel) {
                this.updatePositionTimer.cancel();
            }
            else {
                clearTimeout(this.updatePositionTimer);
                this.updatePositionTimer = null;
            }
        }
        if (this.delayTimer) {
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
        }
        if (this.mouseDownTimeout) {
            clearTimeout(this.mouseDownTimeout);
            this.mouseDownTimeout = null;
        }
    };
    Trigger.prototype.clearDelayTimer = function () {
        if (this.delayTimer) {
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
        }
    };
    Trigger.prototype.render = function () {
        var _this = this;
        var _a = this.props, children = _a.children, style = _a.style, className = _a.className, blurToHide = _a.blurToHide, disabled = _a.disabled, trigger = _a.trigger, popup = _a.popup, classNames = _a.classNames, duration = _a.duration, unmountOnExit = _a.unmountOnExit, position = _a.position, popupHoverStay = _a.popupHoverStay, childrenPrefix = _a.childrenPrefix, showArrow = _a.showArrow, dropdownPopupStyle = _a.popupStyle;
        var getPrefixCls = this.context.getPrefixCls;
        var _b = this.state, popupVisible = _b.popupVisible, popupStyle = _b.popupStyle;
        if (!popup) {
            return null;
        }
        var mergeProps = {};
        var popupEventProps = {
            onMouseDown: this.onPopupMouseDown,
        };
        if (!disabled) {
            switch (trigger) {
                case 'hover':
                    mergeProps.onMouseEnter = this.onMouseEnter;
                    mergeProps.onMouseLeave = this.onMouseLeave;
                    if (popupHoverStay) {
                        popupEventProps.onMouseEnter = this.onPopupMouseEnter;
                        popupEventProps.onMouseLeave = this.onPopupMouseLeave;
                    }
                    break;
                case 'click':
                    mergeProps.onClick = this.onClick;
                    // mergeProps.onFocus = this.onFocus;
                    break;
                case 'focus':
                    mergeProps.onFocus = this.onFocus;
                    if (blurToHide) {
                        mergeProps.onBlur = this.onBlur;
                    }
                    break;
                default:
                    break;
            }
        }
        var child = this.getChild();
        var popupChildren = React.Children.only(popup());
        if (child.props.className) {
            mergeProps.className = child.props.className;
        }
        if (childrenPrefix && popupVisible) {
            mergeProps.className = mergeProps.className
                ? mergeProps.className + " " + childrenPrefix + "-open"
                : childrenPrefix + "-open";
        }
        var prefixCls = getPrefixCls('trigger');
        var popupClassName = cs(className, prefixCls, childrenPrefix, prefixCls + "-position-" + position);
        var childrenComponent = children && (React.createElement(ResizeObserver, { onResize: this.onResize }, React.cloneElement(child, __assign(__assign({}, mergeProps), { tabIndex: disabled ? -1 : 0 }))));
        var portalContent = (React.createElement(CSSTransition, { in: !!popupVisible, timeout: duration, classNames: classNames, unmountOnExit: unmountOnExit, mountOnEnter: true, onEnter: function (e) {
                e.style.display = 'initial';
            }, onExited: function (e) {
                e.style.display = 'none';
                _this.setState({ popupStyle: {} });
            } },
            React.createElement(ResizeObserver, { onResize: this.onResize },
                React.createElement("span", __assign({ ref: function (node) { return (_this.triggerRef = node); }, "trigger-placement": this.realPosition, style: __assign(__assign(__assign({}, popupStyle), { position: 'absolute' }), style) }, popupEventProps, { className: popupClassName }),
                    React.createElement(popupChildren.type, __assign({}, popupChildren.props, { style: __assign(__assign({}, popupChildren.props.style), dropdownPopupStyle) })),
                    showArrow && (React.createElement("div", { className: prefixCls + "-arrow " + childrenPrefix + "-arrow", style: __assign({}, this.arrowStyle) }))))));
        var portal = (React.createElement(Portal, { forceRender: true, visible: popupVisible, getContainer: this.getContainer }, portalContent));
        return children ? (React.createElement(React.Fragment, null,
            childrenComponent,
            portal)) : (portal);
    };
    Trigger.displayName = 'Trigger';
    Trigger.contextType = ConfigContext;
    Trigger.propTypes = {
        style: PropTypes.object,
        popupStyle: PropTypes.object,
        className: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
        autoAlignPopupWidth: PropTypes.bool,
        autoAlignPopupMinWidth: PropTypes.bool,
        blurToHide: PropTypes.bool,
        classNames: PropTypes.string,
        duration: PropTypes.number,
        childrenPrefix: PropTypes.string,
        mouseEnterDelay: PropTypes.number,
        mouseLeaveDelay: PropTypes.number,
        focusDelay: PropTypes.number,
        getPopupContainer: PropTypes.func,
        popupVisible: PropTypes.bool,
        trigger: PropTypes.oneOf(triggerList),
        position: PropTypes.oneOf(positionList),
        popupAlign: PropTypes.object,
        onVisibleChange: PropTypes.func,
        disabled: PropTypes.bool,
        popupHoverStay: PropTypes.bool,
        autoFixPosition: PropTypes.bool,
        showArrow: PropTypes.bool,
        clickToClose: PropTypes.bool,
    };
    Trigger.defaultProps = {
        style: {},
        popupStyle: {},
        blurToHide: true,
        classNames: 'fade',
        trigger: 'hover',
        position: 'bottom',
        duration: 200,
        unmountOnExit: true,
        popupAlign: {},
        disabled: false,
        popupHoverStay: true,
        getDocument: function () { return window.document; },
        autoFixPosition: true,
        showArrow: false,
        mouseEnterDelay: 100,
        mouseLeaveDelay: 100,
        clickToClose: true,
    };
    return Trigger;
}(PureComponent));
export default Trigger;
